import { useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMutation } from '@/utils/api';

// Types for recovery codes
export interface RecoveryCodesResponse {
  codes?: string[];
  generated_at?: string;
  total_codes?: number;
  used_codes?: number;
  valid?: boolean;
}

export interface ValidateRecoveryCodeRequest {
  action: 'validate';
  code: string;
}

export interface GenerateRecoveryCodesRequest {
  action: 'generate';
  count?: number;
}

export interface GetRecoveryCodesInfoRequest {
  action: 'info';
}

export type RecoveryCodesRequest =
  | ValidateRecoveryCodeRequest
  | GenerateRecoveryCodesRequest
  | GetRecoveryCodesInfoRequest;

// Hook for generating recovery codes
export function useGenerateRecoveryCodes() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<RecoveryCodesResponse, Error, { count?: number }>({
    defaultErrorMessage: 'Failed to generate recovery codes',
    mutationFn: async ({ count = 8 }) => {
      const response = await axios.post('/recovery-codes', {
        action: 'generate',
        count,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Recovery codes generated successfully');
    },
  });
}

// Hook for validating recovery codes
export function useValidateRecoveryCode() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<null, Error, { code: string }>({
    defaultErrorMessage: 'Invalid recovery code',
    mutationFn: async ({ code }) => {
      await axios.post('/recovery-codes', {
        action: 'validate',
        code,
      });
      return null;
    },
    onSuccess: () => {
      toast.success('Recovery code validated successfully');
    },
  });
}

// Hook for validating recovery codes during MFA challenge
export function useValidateRecoveryCodeMFA() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<null, Error, { username: string; code: string }>({
    defaultErrorMessage: 'Invalid recovery code',
    mutationFn: async ({ username, code }) => {
      await axios.post('/recovery-codes/validate-mfa', {
        username,
        code,
      });
      return null;
    },
    onSuccess: () => {
      toast.success('Recovery code validated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Recovery code validation failed: ${error.message}`);
    },
  });
}

// Hook for getting recovery codes info
export function useGetRecoveryCodesInfo() {
  const axios = useAxios({ doNotImpersonate: true });
  const [isLoading, setIsLoading] = useState(false);

  const getInfo = async (): Promise<RecoveryCodesResponse> => {
    setIsLoading(true);
    try {
      const response = await axios.post('/recovery-codes', {
        action: 'info',
      });
      return response.data;
    } catch (error) {
      toast.error('Failed to get recovery codes info');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getInfo,
    isLoading,
  };
}
